import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DURATION_ALL_VALUE,
  DURATION_FILTER_OPTIONS,
  type DurationRange,
} from '@/utils/topic-duration';
import {
  CONTENT_CATEGORY_LABELS,
  type ContentCategory,
} from '@shared/api.interface';
import { TOPIC_FILTER_LABELS } from './topic-filter-copy';

interface TopicFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  durationRanges: DurationRange[];
  category: ContentCategory | '';
  resultCount: number;
  onDurationToggle: (value: DurationRange | typeof DURATION_ALL_VALUE) => void;
  onCategoryChange: (value: ContentCategory | '') => void;
  onClear: () => void;
}

export function TopicFilterDrawer({
  open,
  onOpenChange,
  durationRanges,
  category,
  resultCount,
  onDurationToggle,
  onCategoryChange,
  onClear,
}: TopicFilterDrawerProps) {
  const hasActiveFilters = durationRanges.length > 0 || !!category;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[82vh] rounded-t-3xl">
        <DrawerHeader className="border-b border-orange-100 px-4 pb-3 pt-5 text-left">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DrawerTitle className="text-base">筛选</DrawerTitle>
              <DrawerDescription className="sr-only">按主题和时长筛选</DrawerDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              disabled={!hasActiveFilters}
              className="h-8 px-2 text-xs text-orange-700 hover:bg-orange-50 hover:text-orange-800 disabled:text-muted-foreground"
            >
              清空
            </Button>
          </div>
        </DrawerHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-6 px-4 py-5">
            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">{TOPIC_FILTER_LABELS.category}</h3>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onCategoryChange('')}
                  className={`h-10 rounded-full border px-3 text-sm font-medium transition-colors ${
                    category === ''
                      ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                      : 'border-orange-100 bg-white text-foreground hover:border-orange-300 hover:text-orange-700'
                  }`}
                >
                  全部
                </button>
                {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onCategoryChange(key as ContentCategory)}
                    className={`h-10 rounded-full border px-3 text-sm font-medium transition-colors ${
                      category === key
                        ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                        : 'border-orange-100 bg-white text-foreground hover:border-orange-300 hover:text-orange-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">{TOPIC_FILTER_LABELS.duration}</h3>
              <div className="grid grid-cols-2 gap-2">
                {DURATION_FILTER_OPTIONS.map((option) => {
                  const isActive =
                    option.value === DURATION_ALL_VALUE
                      ? durationRanges.length === 0
                      : durationRanges.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onDurationToggle(option.value)}
                      className={`h-10 rounded-full border px-3 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                          : 'border-orange-100 bg-white text-foreground hover:border-orange-300 hover:text-orange-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </ScrollArea>

        <DrawerFooter className="border-t border-orange-100 bg-white px-4 py-3">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-11 rounded-xl bg-orange-500 font-semibold text-white hover:bg-orange-600"
          >
            查看结果（{resultCount}）
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
